import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { getMemberSubscriptionStatus } from '../services/api';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const MembershipSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, failed
  const [subscriptionData, setSubscriptionData] = useState(null);
  
  const sessionId = searchParams.get('session_id');

  const checkStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }
    
    try {
      const response = await getMemberSubscriptionStatus(sessionId);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setSubscriptionData(response.data);
        toast.success('Subscription activated successfully!');
      } else if (response.data.status === 'complete') {
        setStatus('success');
        setSubscriptionData(response.data);
      } else {
        // Poll again after delay
        setTimeout(checkStatus, 2000);
      }
    } catch (error) {
      console.error('Failed to check status:', error);
      setStatus('failed');
    }
  }, [sessionId]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  if (status === 'loading') {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64">
          <Loader2 className="w-12 h-12 text-red-600 animate-spin mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Processing Payment</h2>
          <p className="text-zinc-400">Please wait while we confirm your subscription...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (status === 'failed') {
    return (
      <DashboardLayout>
        <div className="max-w-md mx-auto text-center py-16">
          <div className="w-16 h-16 mx-auto mb-6 bg-red-600/20 rounded-full flex items-center justify-center">
            <XCircle className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Payment Failed</h2>
          <p className="text-zinc-400 mb-8">
            We couldn't process this subscription. Please try again or contact support.
          </p>
          <Button
            onClick={() => navigate('/membership-plans')}
            className="bg-red-600 hover:bg-red-700"
          >
            Back to Membership Plans
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-md mx-auto text-center py-16" data-testid="subscription-success">
        <div className="w-16 h-16 mx-auto mb-6 bg-green-600/20 rounded-full flex items-center justify-center">
          <CheckCircle className="w-10 h-10 text-green-500" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-4">Subscription Activated!</h2>
        <p className="text-zinc-400 mb-8">
          The member has been successfully subscribed. Recurring payments are now active.
        </p>
        <div className="space-y-4">
          <Button
            onClick={() => navigate('/membership-plans')}
            className="bg-red-600 hover:bg-red-700 w-full"
          >
            View All Subscriptions
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/attendance')}
            className="border-zinc-700 w-full"
          >
            Go to Attendance
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MembershipSuccessPage;
